import * as React from 'react';
import { useState } from 'react';

/** Material UI Packages. */
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';

/** Material UI Icons. */
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

/** Date formatting. */
import dayjs from 'dayjs';
import { randomId } from '@mui/x-data-grid-generator';

/** Matches the alias column length. */
const ALIAS_MAX_LENGTH = 255;

/**
 * Collapses spacing and case so "ABC  Inc." and "abc inc." count as the same alias in this list.
 * The server applies its own normalization when it checks aliases across all trade partners.
 */
export const normalizeAlias = (value) => (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

/** Where the alias first came from: the packing list it was registered on, if that list still exists. */
const sourceLabel = (alias) => {
    const parts = [alias.source_hbl_no, alias.source_invoice_no && `INV ${alias.source_invoice_no}`].filter(Boolean);
    return parts.length > 0 ? `PL ${parts.join(' / ')}` : null;
};

const AliasRow = ({ alias, onDelete }) => {
    const isNew = typeof alias.id !== 'number';
    const meta = isNew
        ? ['Not saved yet']
        : [
              sourceLabel(alias),
              alias.created_by_name,
              alias.created_at && dayjs(alias.created_at).format('MM/DD/YY HH:mm'),
          ].filter(Boolean);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.5,
                px: 1,
                py: 0.5,
                borderRadius: '6px',
                border: '1px solid',
                borderColor: alias.conflict_with ? '#d32f2f' : '#e0e0e0',
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, wordBreak: 'break-word' }}>{alias.alias}</Typography>
                {meta.length > 0 && (
                    <Typography sx={{ fontSize: 11, color: '#8a8a8a' }}>{meta.join(' · ')}</Typography>
                )}
                {alias.conflict_with && (
                    <Typography sx={{ fontSize: 11, color: '#d32f2f' }}>
                        Already used by {alias.conflict_with}
                    </Typography>
                )}
            </Box>
            <Tooltip title="Remove alias">
                <IconButton size="small" onClick={() => onDelete(alias.id)} sx={{ p: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

/**
 * Names this trade partner appears under on packing lists.
 *
 * Adding and removing only changes the edit form; the list is sent with the dialog's Save.
 * The list stays read-only until the saved aliases have been loaded, so a save never sends
 * a partial list that would remove aliases the form has not seen.
 */
export default function AliasList(props) {
    /** Get props from PartnerForm.js. */
    const { state, setState, inputObj } = props;

    const [draft, setDraft] = useState('');
    const [draftError, setDraftError] = useState('');

    const aliases = state[inputObj.field];
    const loaded = Array.isArray(aliases);
    const loadFailed = aliases === null;

    /** Any edit makes the conflict marks from the last failed save out of date. */
    const writeAliases = (next) => {
        setState((prev) => ({
            ...prev,
            [inputObj.field]: next.map(({ conflict_with, ...rest }) => rest),
        }));
    };

    const handleAdd = () => {
        const value = draft.trim().replace(/\s+/g, ' ');
        if (!value) return;
        if (aliases.some((item) => normalizeAlias(item.alias) === normalizeAlias(value))) {
            setDraftError('This alias is already in the list.');
            return;
        }
        writeAliases([...aliases, { id: randomId(), alias: value }]);
        setDraft('');
        setDraftError('');
    };

    const handleDelete = (id) => {
        writeAliases(aliases.filter((item) => item.id !== id));
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
            event.preventDefault();
            handleAdd();
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
            <Typography sx={{ color: '#003a52', fontSize: 13, fontWeight: 500 }}>{inputObj.name}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <TextField
                    value={draft}
                    onChange={(e) => {
                        setDraft(e.target.value);
                        setDraftError('');
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder="Name as written on the document"
                    disabled={!loaded}
                    error={Boolean(draftError)}
                    helperText={draftError || undefined}
                    size="small"
                    slotProps={{ htmlInput: { maxLength: ALIAS_MAX_LENGTH } }}
                    sx={{
                        flex: 1,
                        '& .MuiInputBase-input': { fontSize: 13, py: 0.75 },
                        '& .MuiFormHelperText-root': { fontSize: 11, mx: 0 },
                    }}
                />
                <Tooltip title="Add alias">
                    <span>
                        <IconButton size="small" onClick={handleAdd} disabled={!loaded || !draft.trim()}>
                            <AddIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
            {!loaded && !loadFailed && (
                <Typography sx={{ fontSize: 12, color: '#8a8a8a' }}>Loading aliases...</Typography>
            )}
            {loadFailed && (
                <Typography sx={{ fontSize: 12, color: '#d32f2f' }}>
                    Could not load aliases. Reopen this dialog to try again.
                </Typography>
            )}
            {loaded && aliases.length === 0 && (
                <Typography sx={{ fontSize: 12, color: '#8a8a8a' }}>No aliases registered.</Typography>
            )}
            {loaded &&
                aliases.map((alias) => <AliasRow key={alias.id} alias={alias} onDelete={handleDelete} />)}
        </Box>
    );
}
